/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useFieldArray, useForm, useWatch } from "react-hook-form";
import {
  mapFromAccountsResponse,
  mapGroupedAccountsToServiceOptions,
  mapPreviewResponse,
  mapToAccountsResponse,
  useCreateTransferMutation,
  useGetAccountsForTransferFromQuery,
  useLazyGetAccountBalanceQuery,
  useLazyGetAccountsForTransferToQuery,
  usePreviewTransferMutation,
  type TransferAccountOption,
  type TransferCreateRequest,
  type TransferGroupedAccounts,
  type TransferPreview,
  type TransferPreviewRequest,
  type TransferServiceOption,
} from "src/entities/transfer";
import { sendTransferDirtyChanged } from "src/shared/lib/iframe/transferIframeBridge";
import type { TransferMoneyFormValues } from "./types";

type AccountToBalanceInfo = {
  balance: number;
  currency: string;
};

type UseTransferMoneyFormParams = {
  clientId: string;
  onCancel?: () => void;
  onSuccess?: (values: TransferMoneyFormValues) => void;
  onResult?: (result: {
    status: "success" | "partial_success" | "error";
    message: string;
    transfersCount?: number;
    successCount?: number;
    failedCount?: number;
  }) => void;
};

const getSubmitErrorMessage = (error: any): string => {
  const data = error?.data;

  if (typeof data?.message === "string") {
    return data.message;
  }

  if (
    data?.data?.errors &&
    Array.isArray(data.data.errors) &&
    data.data.errors.length
  ) {
    const firstError = data.data.errors[0];

    if (typeof firstError?.error === "string") {
      return firstError.error;
    }
  }

  if (data?.errors && typeof data.errors === "object") {
    const firstFieldErrors = Object.values(data.errors)[0];

    if (
      Array.isArray(firstFieldErrors) &&
      typeof firstFieldErrors[0] === "string"
    ) {
      return firstFieldErrors[0];
    }
  }

  return "Не удалось выполнить перевод";
};

const getPreviewErrorMessage = (error: any): string => {
  const data = error?.data;

  if (typeof data?.message === "string") {
    return data.message;
  }

  if (Array.isArray(data?.errors) && data.errors.length > 0) {
    const firstError = data.errors[0];

    if (typeof firstError === "string") {
      return firstError;
    }

    if (firstError && typeof firstError.error === "string") {
      return firstError.error;
    }
  }

  if (data?.errors && typeof data.errors === "object") {
    const firstFieldErrors = Object.values(data.errors)[0];

    if (
      Array.isArray(firstFieldErrors) &&
      typeof firstFieldErrors[0] === "string"
    ) {
      return firstFieldErrors[0];
    }
  }

  return "Не удалось получить превью перевода";
};

const defaultValues: TransferMoneyFormValues = {
  servicesFrom: null,
  accountFromSelect: null,
  balanceFrom: null,
  serviceTo: null,
  accountsTo: [],
};

export const useTransferMoneyForm = ({
  clientId,
  onCancel,
  onSuccess,
  onResult,
}: UseTransferMoneyFormParams) => {
  const resetPreviewState = useCallback(() => {
    setPreviewData(null);
    setPreviewErrorMessage(null);
  }, []);

  const form = useForm<TransferMoneyFormValues>({
    mode: "onChange",
    defaultValues,
  });

  const {
    control,
    watch,
    setValue,
    resetField,
    handleSubmit,
    reset,
    formState: { isDirty, isValid, errors },
  } = form;

  const fieldArray = useFieldArray({
    control,
    name: "accountsTo",
  });

  const { fields, append, remove, replace } = fieldArray;

  const {
    data: fromAccountsResponse,
    isLoading: isFromAccountsLoading,
    isFetching: isFromAccountsFetching,
    isError: isFromAccountsError,
    refetch: refetchFromAccounts,
  } = useGetAccountsForTransferFromQuery({ clientId }, { skip: !clientId });

  const [getBalance, balanceQuery] = useLazyGetAccountBalanceQuery();
  const [getAccountsTo, toAccountsQuery] =
    useLazyGetAccountsForTransferToQuery();
  const [getAccountToBalance] = useLazyGetAccountBalanceQuery();
  const [previewTransfer, previewQuery] = usePreviewTransferMutation();
  const [createTransfer, createTransferQuery] = useCreateTransferMutation();

  const [submitErrorMessage, setSubmitErrorMessage] = useState<string | null>(
    null,
  );
  const [submitResultMessage, setSubmitResultMessage] = useState<string | null>(
    null,
  );
  const [previewData, setPreviewData] = useState<TransferPreview | null>(null);
  const [previewErrorMessage, setPreviewErrorMessage] = useState<string | null>(
    null,
  );
  const [accountFromOptions, setAccountFromOptions] = useState<
    TransferAccountOption[]
  >([]);
  const [groupedToAccounts, setGroupedToAccounts] =
    useState<TransferGroupedAccounts>({});
  const [accountToOptions, setAccountToOptions] = useState<
    TransferAccountOption[]
  >([]);
  const [accountToBalances, setAccountToBalances] = useState<
    Record<number, AccountToBalanceInfo | null>
  >({});
  const [accountToBalanceLoading, setAccountToBalanceLoading] = useState<
    Record<number, boolean>
  >({});
  const [isCloseConfirmOpen, setIsCloseConfirmOpen] = useState(false);
  const [isSubmitSuccessOpen, setIsSubmitSuccessOpen] = useState(false);

  const servicesFrom = watch("servicesFrom");
  const accountFromSelect = watch("accountFromSelect");
  const balanceFrom = watch("balanceFrom");
  const serviceTo = watch("serviceTo");

  const accountsTo = useWatch({
    control,
    name: "accountsTo",
  });

  const previewRequestBody = useMemo<TransferPreviewRequest | null>(() => {
    if (!accountFromSelect) return null;
    if (!accountsTo?.length) return null;

    const accountsForTransfer = accountsTo
      .filter(
        (item) =>
          !!item?.accountTo &&
          !!item?.currency &&
          item?.sum !== "" &&
          Number(item.sum) > 0,
      )
      .map((item) => ({
        account_to_id: item.accountTo!.psaaId,
        amount: Number(item.sum),
        currency: item.currency!,
      }));

    if (!accountsForTransfer.length) return null;
    if (accountsForTransfer.length !== accountsTo.length) return null;

    return {
      account_from_id: accountFromSelect.psaaId,
      accounts_for_transfer: accountsForTransfer,
    };
  }, [accountFromSelect, accountsTo]);

  const createTransferBody = useMemo<TransferCreateRequest | null>(() => {
    if (!accountFromSelect) return null;
    if (!accountsTo?.length) return null;

    const accountsForTransfer = accountsTo
      .filter(
        (item) =>
          !!item?.accountTo &&
          !!item?.currency &&
          item?.sum !== "" &&
          Number(item.sum) > 0,
      )
      .map((item) => ({
        account_to_id: item.accountTo!.psaaId,
        amount: Number(item.sum),
        currency: item.currency!,
      }));

    if (!accountsForTransfer.length) return null;
    if (accountsForTransfer.length !== accountsTo.length) return null;

    return {
      account_from_id: accountFromSelect.psaaId,
      accounts_for_transfer: accountsForTransfer,
    };
  }, [accountFromSelect, accountsTo]);

  const groupedFromAccounts = useMemo<TransferGroupedAccounts>(() => {
    if (!fromAccountsResponse) return {};

    return mapFromAccountsResponse(fromAccountsResponse);
  }, [fromAccountsResponse]);

  const serviceFromOptions = useMemo<TransferServiceOption[]>(() => {
    return mapGroupedAccountsToServiceOptions(groupedFromAccounts);
  }, [groupedFromAccounts]);

  const serviceToOptions = useMemo<TransferServiceOption[]>(() => {
    return mapGroupedAccountsToServiceOptions(groupedToAccounts);
  }, [groupedToAccounts]);

  const fetchAccountToBalance = useCallback(
    async (account: TransferAccountOption | null) => {
      if (!account) return;

      setAccountToBalanceLoading((prev) => ({
        ...prev,
        [account.psaaId]: true,
      }));

      try {
        const result = await getAccountToBalance({
          clientId,
          accountBelongsToClient: account.psaaId,
        }).unwrap();

        setAccountToBalances((prev) => ({
          ...prev,
          [account.psaaId]: {
            balance: result.data.balance,
            currency: result.data.currency,
          },
        }));
      } catch {
        setAccountToBalances((prev) => ({
          ...prev,
          [account.psaaId]: null,
        }));
      } finally {
        setAccountToBalanceLoading((prev) => ({
          ...prev,
          [account.psaaId]: false,
        }));
      }
    },
    [clientId, getAccountToBalance],
  );

  const resetDependentFieldsAfterServiceFrom = () => {
    resetField("accountFromSelect");
    resetField("balanceFrom");
    resetField("serviceTo");
    replace([]);
    setGroupedToAccounts({});
    setAccountToOptions([]);
    setAccountToBalances({});
    setAccountToBalanceLoading({});
    resetPreviewState();
    setSubmitErrorMessage(null);
    setSubmitResultMessage(null);
  };

  const resetDependentFieldsAfterAccountFrom = () => {
    resetField("balanceFrom");
    resetField("serviceTo");
    replace([]);
    setGroupedToAccounts({});
    setAccountToOptions([]);
    setAccountToBalances({});
    setAccountToBalanceLoading({});
    resetPreviewState();
    setSubmitErrorMessage(null);
    setSubmitResultMessage(null);
  };

  const handleServiceFromChange = (option: TransferServiceOption | null) => {
    setValue("servicesFrom", option, {
      shouldDirty: true,
      shouldValidate: true,
    });

    resetDependentFieldsAfterServiceFrom();

    if (!option) {
      setAccountFromOptions([]);

      return;
    }

    setAccountFromOptions(groupedFromAccounts[option.value] ?? []);
  };

  const handleAccountFromChange = async (
    option: TransferAccountOption | null,
  ) => {
    setValue("accountFromSelect", option, {
      shouldDirty: true,
      shouldValidate: true,
    });

    resetDependentFieldsAfterAccountFrom();

    if (!option) return;

    try {
      const [balanceResult, toAccountsResult] = await Promise.all([
        getBalance({
          clientId,
          accountBelongsToClient: option.psaaId,
        }).unwrap(),
        getAccountsTo({
          clientId,
          accountFromId: option.psaaId,
        }).unwrap(),
      ]);

      setValue("balanceFrom", balanceResult.data.balance, {
        shouldDirty: true,
        shouldValidate: true,
      });

      setGroupedToAccounts(mapToAccountsResponse(toAccountsResult));
    } catch {
      setValue("balanceFrom", null, {
        shouldDirty: true,
        shouldValidate: true,
      });
      setGroupedToAccounts({});
      setAccountToOptions([]);
      setAccountToBalances({});
      setAccountToBalanceLoading({});
    }
  };

  const handleServiceToChange = (option: TransferServiceOption | null) => {
    setValue("serviceTo", option, { shouldDirty: true, shouldValidate: true });
    replace([]);
    setAccountToOptions([]);
    setAccountToBalances({});
    setAccountToBalanceLoading({});
    resetPreviewState();
    setSubmitErrorMessage(null);
    setSubmitResultMessage(null);

    if (!option) return;

    const nextOptions = groupedToAccounts[option.value] ?? [];
    setAccountToOptions(nextOptions);

    if (nextOptions.length) {
      replace([
        {
          accountTo: null,
          sum: "",
          currency: null,
        },
      ]);
    }
  };

  const handleCancelClick = useCallback(() => {
    if (!isDirty) {
      onCancel?.();

      return;
    }

    setIsCloseConfirmOpen(true);
  }, [isDirty, onCancel]);

  useEffect(() => {
    sendTransferDirtyChanged(isDirty);
  }, [isDirty]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (!event.data || typeof event.data !== "object") return;

      if (event.data.type === "TRANSFER_PARENT_CLOSE_ATTEMPT") {
        handleCancelClick();
      }
    };

    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [handleCancelClick]);

  const handleCloseConfirm = () => {
    setIsCloseConfirmOpen(false);
  };

  const handleConfirmClose = () => {
    setIsCloseConfirmOpen(false);
    onCancel?.();
  };

  const handleSuccessSnackbarClose = () => {
    setIsSubmitSuccessOpen(false);
  };

  useEffect(() => {
    accountsTo?.forEach((item, index) => {
      const nextCurrency = item?.accountTo?.currency ?? null;
      const currentCurrency = item?.currency ?? null;

      if (nextCurrency !== currentCurrency) {
        setValue(`accountsTo.${index}.currency`, nextCurrency, {
          shouldDirty: true,
          shouldValidate: false,
        });
      }
    });
  }, [accountsTo, setValue]);

  useEffect(() => {
    const selectedAccounts =
      accountsTo?.map((item) => item?.accountTo).filter(Boolean) ?? [];

    selectedAccounts.forEach((account) => {
      if (!account) return;

      const hasBalance = account.psaaId in accountToBalances;
      const isLoading = !!accountToBalanceLoading[account.psaaId];

      if (!hasBalance && !isLoading) {
        void fetchAccountToBalance(account);
      }
    });
  }, [
    accountsTo,
    accountToBalances,
    accountToBalanceLoading,
    fetchAccountToBalance,
  ]);

  useEffect(() => {
    if (!clientId || !previewRequestBody) {
      setPreviewData(null);
      setPreviewErrorMessage(null);

      return;
    }

    resetPreviewState();

    const timeoutId = window.setTimeout(async () => {
      try {
        const response = await previewTransfer({
          clientId,
          body: previewRequestBody,
        }).unwrap();

        const mappedPreview = mapPreviewResponse(response);
        setPreviewData(mappedPreview);

        if (!mappedPreview.validationResult.isValid) {
          setPreviewErrorMessage(
            mappedPreview.validationResult.errors[0]?.error ??
              "Перевод не прошел проверку",
          );
        } else {
          setPreviewErrorMessage(null);
        }
      } catch (error: any) {
        setPreviewData(null);
        setPreviewErrorMessage(getPreviewErrorMessage(error));
      }
    }, 500);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [clientId, previewRequestBody, previewTransfer, resetPreviewState]);

  const submit = async (values: TransferMoneyFormValues) => {
    setSubmitErrorMessage(null);
    setSubmitResultMessage(null);

    if (!clientId || !createTransferBody) {
      const message = "Недостаточно данных для выполнения перевода";
      setSubmitErrorMessage(message);

      onResult?.({
        status: "error",
        message,
      });

      return;
    }

    try {
      const response = await createTransfer({
        clientId,
        body: createTransferBody,
      }).unwrap();

      const successItems = response.data.filter((item) => item.is_success);
      const failedItems = response.data.filter((item) => !item.is_success);

      if (successItems.length && !failedItems.length) {
        const message =
          "Операция успешно принята к исполнению, результат вы можете найти на странице 'История переводов'";

        setIsSubmitSuccessOpen(true);
        setSubmitResultMessage(message);

        onResult?.({
          status: "success",
          message,
          transfersCount: response.data.length,
          successCount: successItems.length,
          failedCount: 0,
        });

        onSuccess?.(values);

        reset(defaultValues);
        replace([]);
        setAccountFromOptions([]);
        setGroupedToAccounts({});
        setAccountToOptions([]);
        setAccountToBalances({});
        setAccountToBalanceLoading({});
        setPreviewData(null);
        setPreviewErrorMessage(null);

        return;
      }

      if (successItems.length && failedItems.length) {
        const message = `Частичный успех: успешно ${successItems.length}, с ошибкой ${failedItems.length}`;

        setSubmitResultMessage(message);

        onResult?.({
          status: "partial_success",
          message,
          transfersCount: response.data.length,
          successCount: successItems.length,
          failedCount: failedItems.length,
        });

        return;
      }

      const message = "Не удалось создать ни одного перевода";
      setSubmitErrorMessage(message);

      onResult?.({
        status: "error",
        message,
        transfersCount: response.data.length,
        successCount: 0,
        failedCount: failedItems.length,
      });
    } catch (error: any) {
      const message = getSubmitErrorMessage(error);
      setSubmitErrorMessage(message);

      onResult?.({
        status: "error",
        message,
      });
    }
  };

  const isSubmitDisabled =
    !isDirty ||
    !isValid ||
    !servicesFrom ||
    !accountFromSelect ||
    !serviceTo ||
    !fields.length ||
    previewQuery.isLoading ||
    createTransferQuery.isLoading ||
    !previewData ||
    !previewData.validationResult.isValid ||
    (previewData.accountFrom.newBalance ?? 0) < 0;

  return {
    form,
    fields,
    append,
    remove,

    serviceFromOptions,
    accountFromOptions,
    serviceToOptions,
    accountToOptions,

    servicesFrom,
    accountFromSelect,
    balanceFrom,
    serviceTo,

    accountToBalances,
    accountToBalanceLoading,

    errors,
    isDirty,
    isValid,
    isSubmitDisabled,

    isCloseConfirmOpen,
    isSubmitSuccessOpen,

    isInitialLoading: isFromAccountsLoading || isFromAccountsFetching,
    isInitialError: isFromAccountsError,
    isAccountDataLoading: balanceQuery.isFetching || toAccountsQuery.isFetching,
    isAccountDataError: balanceQuery.isError || toAccountsQuery.isError,
    refetchInitialData: refetchFromAccounts,

    handleSubmit,
    handleServiceFromChange,
    handleAccountFromChange,
    handleServiceToChange,
    handleCancelClick,
    handleCloseConfirm,
    handleConfirmClose,
    handleSuccessSnackbarClose,
    submit,
    previewData,
    previewErrorMessage,
    isPreviewLoading: previewQuery.isLoading,
    isPreviewError: previewQuery.isError,
    submitErrorMessage,
    submitResultMessage,
    isSubmittingTransfer: createTransferQuery.isLoading,
  };
};
